/**
 * WebRTCBackend Implementation
 *
 * Backend implementation for web clients connecting to remote machines via WebRTC.
 * Proxies all commands through the WebRTC data channel to the remote machine.
 */

import { peerConnection } from '../peerConnection';
import type {
  IBackend,
  IProjectBackend,
  IFileBackend,
  ITranscriptionBackend,
  ISystemBackend,
  ITerminalBackend,
  IPipelineBackend,
  PipelineConfig,
  Ticket,
  TicketState,
  Transcript,
  AgentRun,
  AgentProfiles,
  PipelineRun,
  WorktreeInfo,
  TicketDiff,
  Runner,
  RunnerResult,
  PipelineTemplate,
  BackendType,
  GeneratedProjectDetails,
  AgentModelInfo,
  LogEntry,
  IQueueBackend,
  QueueTicketSummary,
  QueueEpicSummary,
  QueueIdeaSummary,
  QueueRunInfo,
  IIdentityBackend,
  ICopilotIdentityBackend,
  CopilotIdentity,
  CopilotAuthStatus,
  IGroupBackend,
  IArtifactsBackend,
  ArtifactEntry,
  ProjectGroup,
  ClaudeIdentity,
  AuthStatus,
} from './IBackend';
import type {
  Project,
  CreateProjectInput,
  UpdateProjectInput,
  AgentInfo,
  ProjectAnalysisResult,
  FileNode,
  FileIndexEntry,
  GitFileStatus,
  TranscriptionResult,
  WhisperConfig,
  EnhanceTranscriptionParams,
} from './types';
import type { ProjectStats } from '../../types/tauri';

/**
 * Project backend implementation for WebRTC (remote machine proxy)
 */
class WebRTCProjectBackend implements IProjectBackend {
  async create(input: CreateProjectInput): Promise<Project> {
    return await peerConnection.sendCommand<Project>('create_project', { input });
  }

  async listZips(): Promise<string[]> {
    throw new Error('WebRTC: zip import is not supported on the remote client');
  }
  async createFromZip(): Promise<Project> {
    throw new Error('WebRTC: zip import is not supported on the remote client');
  }
  async extractZip(): Promise<string> {
    throw new Error('WebRTC: zip import is not supported on the remote client');
  }

  async list(): Promise<Project[]> {
    return await peerConnection.sendCommand<Project[]>('get_projects');
  }

  async get(id: string): Promise<Project | null> {
    return await peerConnection.sendCommand<Project | null>('get_project', { id });
  }

  async update(id: string, updates: UpdateProjectInput): Promise<Project> {
    return await peerConnection.sendCommand<Project>('update_project', { id, updates });
  }

  async delete(id: string): Promise<boolean> {
    return await peerConnection.sendCommand<boolean>('delete_project', { id });
  }

  async hasRecentActivity(id: string): Promise<boolean> {
    return await peerConnection.sendCommand<boolean>('has_recent_activity', { id });
  }

  async analyze(path: string): Promise<ProjectAnalysisResult> {
    return await peerConnection.sendCommand<ProjectAnalysisResult>('analyze_project_directory', { path });
  }

  async analyzeWithAI(path: string): Promise<ProjectAnalysisResult> {
    return await peerConnection.sendCommand<ProjectAnalysisResult>('analyze_project_with_ai', { path });
  }

  async generateDetails(projectPath: string): Promise<GeneratedProjectDetails> {
    return await peerConnection.sendCommand<GeneratedProjectDetails>('generate_project_details', { projectPath });
  }

  async generateRunConfigs(projectPath: string): Promise<{ name: string; command: string }[]> {
    return await peerConnection.sendCommand<{ name: string; command: string }[]>('generate_run_configurations', { projectPath });
  }

  async isAIServiceAvailable(): Promise<boolean> {
    return await peerConnection.sendCommand<boolean>('is_ai_service_available');
  }

  async getStats(projectId: string): Promise<ProjectStats> {
    return await peerConnection.sendCommand<ProjectStats>('get_project_stats', { projectId });
  }
}

/**
 * File backend implementation for WebRTC (remote machine proxy)
 */
class WebRTCFileBackend implements IFileBackend {
  async pickFolder(): Promise<string | null> {
    return await peerConnection.sendCommand<string | null>('select_folder');
  }
  async pickFile(): Promise<string | null> {
    // The remote client has no access to the host's native file dialog for a
    // specific file path; zip import is a desktop-local flow.
    return null;
  }

  async readFile(projectId: string, filePath: string): Promise<string> {
    return await peerConnection.sendCommand<string>('read_file_content', { projectId, filePath });
  }

  async getFolderChildren(projectId: string, folderPath: string, parentGitignored?: boolean): Promise<FileNode[]> {
    return await peerConnection.sendCommand<FileNode[]>('get_folder_children', { projectId, folderPath, parentGitignored: parentGitignored ?? false });
  }

  async readProjectFiles(projectId: string): Promise<FileNode[]> {
    return await peerConnection.sendCommand<FileNode[]>('read_project_files', { projectId });
  }

  async crawlProjectTree(projectId: string): Promise<FileNode[]> {
    return await peerConnection.sendCommand<FileNode[]>('crawl_project_tree', { projectId });
  }

  async indexProjectFiles(projectId: string): Promise<FileIndexEntry[]> {
    return await peerConnection.sendCommand<FileIndexEntry[]>('index_project_files', { projectId });
  }

  async getGitStatus(projectId: string): Promise<GitFileStatus[]> {
    return await peerConnection.sendCommand<GitFileStatus[]>('get_git_status', { projectId });
  }

  async writeFile(projectId: string, filePath: string, content: string): Promise<void> {
    await peerConnection.sendCommand<void>('write_file_content', { projectId, filePath, content });
  }

  async readFileBase64(projectId: string, filePath: string): Promise<string> {
    return await peerConnection.sendCommand<string>('read_file_base64', { projectId, filePath });
  }

  async saveAttachment(projectId: string, fileName: string, data: string): Promise<string> {
    return await peerConnection.sendCommand<string>('save_attachment', { projectId, fileName, data });
  }

  async revealInExplorer(path: string): Promise<void> {
    await peerConnection.sendCommand<void>('reveal_in_explorer', { path });
  }

  async openExternal(url: string): Promise<void> {
    await peerConnection.sendCommand<void>('open_external', { url });
  }

  async renameFile(projectId: string, oldPath: string, newName: string): Promise<string> {
    return await peerConnection.sendCommand<string>('rename_file', { projectId, oldPath, newName });
  }

  async deleteFile(projectId: string, filePath: string): Promise<void> {
    await peerConnection.sendCommand<void>('delete_file', { projectId, filePath });
  }

  async createFile(projectId: string, parentPath: string, fileName: string): Promise<string> {
    return await peerConnection.sendCommand<string>('create_file', { projectId, parentPath, fileName });
  }

  async createFolder(projectId: string, parentPath: string, folderName: string): Promise<string> {
    return await peerConnection.sendCommand<string>('create_folder', { projectId, parentPath, folderName });
  }

  async pathExistsInProject(projectId: string, relativePath: string): Promise<boolean> {
    return await peerConnection.sendCommand<boolean>('path_exists_in_project', { projectId, relativePath });
  }

  async copyExternalIntoProject(
    projectId: string,
    sourcePath: string,
    destFolderRelative: string,
    conflictResolution: 'overwrite' | 'keepboth',
  ): Promise<string> {
    return await peerConnection.sendCommand<string>('copy_external_into_project', {
      projectId,
      sourcePath,
      destFolderRelative,
      conflictResolution,
    });
  }

  async moveWithinProject(
    projectId: string,
    sourceRelative: string,
    destFolderRelative: string,
  ): Promise<string> {
    return await peerConnection.sendCommand<string>('move_within_project', {
      projectId,
      sourceRelative,
      destFolderRelative,
    });
  }

  async initAteliercodeConfig(projectId: string, configJson: string, runnersJson: string): Promise<void> {
    await peerConnection.sendCommand('init_ateliercode_config', { projectId, configJson, runnersJson });
  }

  async readAteliercodeRunners(projectId: string): Promise<string | null> {
    return await peerConnection.sendCommand<string | null>('read_ateliercode_runners', { projectId });
  }

  async writeAteliercodeRunners(projectId: string, content: string): Promise<void> {
    await peerConnection.sendCommand('write_ateliercode_runners', { projectId, content });
  }

  async readAteliercodeConfig(projectId: string): Promise<string | null> {
    return await peerConnection.sendCommand<string | null>('read_ateliercode_config', { projectId });
  }

  async writeAteliercodeConfig(projectId: string, content: string): Promise<void> {
    await peerConnection.sendCommand('write_ateliercode_config', { projectId, content });
  }
  async cacheRead(projectId: string, key: string): Promise<string | null> {
    return await peerConnection.sendCommand<string | null>('cache_read', { projectId, key });
  }
  async cacheWrite(projectId: string, key: string, content: string): Promise<void> {
    await peerConnection.sendCommand('cache_write', { projectId, key, content });
  }
  async cacheClear(projectId: string): Promise<void> {
    await peerConnection.sendCommand('cache_clear', { projectId });
  }
  async cacheClearAll(): Promise<number> {
    return await peerConnection.sendCommand<number>('cache_clear_all');
  }
}

/**
 * Transcription backend implementation for WebRTC (remote machine proxy)
 */
class WebRTCTranscriptionBackend implements ITranscriptionBackend {
  async transcribeOpenAI(audioData: string, apiKey: string): Promise<TranscriptionResult> {
    const result = await peerConnection.sendCommand<{ text: string }>('transcribe_openai', { audioData, apiKey });
    return { text: result.text };
  }

  // Cloud (voice-engine) is no longer routed through Tauri/WebRTC — the
  // frontend talks to the cluster directly via @unveiled/voice-engine.

  async getWhisperSettings(): Promise<WhisperConfig> {
    return await peerConnection.sendCommand<WhisperConfig>('get_whisper_settings');
  }

  // Enhanced transcription runs on the host core (which reaches the local AI on
  // the host's network), so forward it over the data channel like transcribe.
  async enhanceTranscription(params: EnhanceTranscriptionParams): Promise<TranscriptionResult> {
    const result = await peerConnection.sendCommand<{ text: string }>('enhance', {
      text: params.text,
      baseUrl: params.baseUrl,
      model: params.model,
      apiKey: params.apiKey ?? null,
      projectName: params.projectName ?? null,
      projectDescription: params.projectDescription ?? null,
    });
    return { text: result.text };
  }
}

/**
 * Pipeline (tickets-as-files) backend for WebRTC. The ticket/config/prompt
 * files live on the host machine, so every call proxies to the host.
 */
class WebRTCPipelineBackend implements IPipelineBackend {
  async getConfig(projectId: string): Promise<PipelineConfig> {
    return await peerConnection.sendCommand<PipelineConfig>('get_pipeline_config', { projectId });
  }

  async setConfig(projectId: string, config: PipelineConfig): Promise<void> {
    await peerConnection.sendCommand<void>('set_pipeline_config', { projectId, config });
  }

  async getPromptTemplate(projectId: string, name: string): Promise<string> {
    return await peerConnection.sendCommand<string>('get_prompt_template', { projectId, name });
  }

  async setPromptTemplate(projectId: string, name: string, content: string): Promise<void> {
    await peerConnection.sendCommand<void>('set_prompt_template', { projectId, name, content });
  }

  async listTickets(projectId: string): Promise<Ticket[]> {
    return await peerConnection.sendCommand<Ticket[]>('list_tickets', { projectId });
  }

  async readTicket(projectId: string, ticketId: string): Promise<Ticket> {
    return await peerConnection.sendCommand<Ticket>('read_ticket', { projectId, ticketId });
  }

  async writeTicket(projectId: string, ticket: Ticket): Promise<Ticket> {
    return await peerConnection.sendCommand<Ticket>('write_ticket', { projectId, ticket });
  }

  async createTicket(
    projectId: string,
    title: string,
    body?: string,
    stage?: string,
    source?: string,
  ): Promise<Ticket> {
    return await peerConnection.sendCommand<Ticket>('create_ticket', {
      projectId,
      title,
      body: body ?? null,
      stage: stage ?? null,
      source: source ?? null,
    });
  }

  async deleteTicket(projectId: string, ticketId: string): Promise<void> {
    await peerConnection.sendCommand<void>('delete_ticket', { projectId, ticketId });
  }

  async setTicketState(
    projectId: string,
    ticketId: string,
    state: TicketState,
    stage?: string,
  ): Promise<Ticket> {
    return await peerConnection.sendCommand<Ticket>('set_ticket_state', { projectId, ticketId, state, stage: stage ?? null });
  }

  async advanceTicket(projectId: string, ticketId: string): Promise<Ticket> {
    return await peerConnection.sendCommand<Ticket>('advance_ticket', { projectId, ticketId });
  }

  async recordTranscript(
    projectId: string,
    source: Transcript['source'],
    title: string,
    content: string,
  ): Promise<Transcript> {
    return await peerConnection.sendCommand<Transcript>('record_transcript', { projectId, source, title, content });
  }

  async listTranscripts(projectId: string): Promise<Transcript[]> {
    return await peerConnection.sendCommand<Transcript[]>('list_transcripts', { projectId });
  }

  async readTranscript(projectId: string, name: string): Promise<string> {
    return await peerConnection.sendCommand<string>('read_transcript', { projectId, name });
  }

  async prepareStageRun(projectId: string, ticketId: string, agentOverride?: string): Promise<AgentRun> {
    return await peerConnection.sendCommand<AgentRun>('prepare_stage_run', { projectId, ticketId, agentOverride: agentOverride ?? null });
  }

  async prepareTriageRun(
    projectId: string,
    transcriptName: string,
    agentOverride?: string,
  ): Promise<AgentRun> {
    return await peerConnection.sendCommand<AgentRun>('prepare_triage_run', { projectId, transcriptName, agentOverride: agentOverride ?? null });
  }

  async getAgentProfiles(projectId: string): Promise<AgentProfiles> {
    return await peerConnection.sendCommand<AgentProfiles>('get_agent_profiles', { projectId });
  }

  async setAgentProfiles(projectId: string, profiles: AgentProfiles): Promise<void> {
    await peerConnection.sendCommand<void>('set_agent_profiles', { projectId, profiles });
  }

  async recordRunStart(input: {
    projectId: string;
    ticketId?: string;
    transcriptName?: string;
    stageId: string;
    kind: 'stage' | 'triage';
    agent?: string;
    ptySessionId?: number;
    prompt?: string;
  }): Promise<string> {
    return await peerConnection.sendCommand<string>('record_run_start', {
      projectId: input.projectId,
      ticketId: input.ticketId ?? null,
      transcriptName: input.transcriptName ?? null,
      stageId: input.stageId,
      kind: input.kind,
      agent: input.agent ?? null,
      ptySessionId: input.ptySessionId ?? null,
      prompt: input.prompt ?? null,
    });
  }

  async recordRunEnd(
    runId: string,
    status: PipelineRun['status'],
    exitCode?: number,
    cost?: string,
  ): Promise<void> {
    await peerConnection.sendCommand<void>('record_run_end', {
      runId,
      status,
      exitCode: exitCode ?? null,
      cost: cost ?? null,
    });
  }

  async listRuns(projectId: string, ticketId?: string): Promise<PipelineRun[]> {
    return await peerConnection.sendCommand<PipelineRun[]>('list_runs', { projectId, ticketId: ticketId ?? null });
  }

  async listActiveRuns(projectId: string): Promise<PipelineRun[]> {
    return await peerConnection.sendCommand<PipelineRun[]>('list_active_runs', { projectId });
  }

  async ensureTicketWorktree(projectId: string, ticketId: string): Promise<WorktreeInfo> {
    return await peerConnection.sendCommand<WorktreeInfo>('ensure_ticket_worktree', { projectId, ticketId });
  }

  async ticketDiff(projectId: string, ticketId: string): Promise<TicketDiff> {
    return await peerConnection.sendCommand<TicketDiff>('ticket_diff', { projectId, ticketId });
  }

  async openTicketPr(projectId: string, ticketId: string, title: string, body: string): Promise<string> {
    return await peerConnection.sendCommand<string>('open_ticket_pr', { projectId, ticketId, title, body });
  }

  async removeTicketWorktree(projectId: string, ticketId: string): Promise<void> {
    await peerConnection.sendCommand<void>('remove_ticket_worktree', { projectId, ticketId });
  }

  async listRunners(projectId: string): Promise<Runner[]> {
    return await peerConnection.sendCommand<Runner[]>('list_runners', { projectId });
  }

  async generateQaRunners(projectPath: string): Promise<Runner[]> {
    return await peerConnection.sendCommand<Runner[]>('generate_qa_runners', { projectPath });
  }

  async runRunner(projectId: string, ticketId: string, runnerId: string): Promise<RunnerResult> {
    return await peerConnection.sendCommand<RunnerResult>('run_runner', { projectId, ticketId, runnerId });
  }

  async readRunLog(projectId: string, runId: string): Promise<string> {
    return await peerConnection.sendCommand<string>('read_run_log', { projectId, runId });
  }

  async exportPipelineTemplate(projectId: string): Promise<PipelineTemplate> {
    return await peerConnection.sendCommand<PipelineTemplate>('export_pipeline_template', { projectId });
  }

  async importPipelineTemplate(projectId: string, template: PipelineTemplate): Promise<PipelineConfig> {
    return await peerConnection.sendCommand<PipelineConfig>('import_pipeline_template', { projectId, template });
  }
}

/**
 * System backend implementation for WebRTC (remote machine proxy)
 */
class WebRTCSystemBackend implements ISystemBackend {
  async detectAgents(): Promise<AgentInfo[]> {
    return await peerConnection.sendCommand<AgentInfo[]>('detect_agents');
  }

  async getHostname(): Promise<string> {
    return await peerConnection.sendCommand<string>('get_hostname');
  }

  async getPlatform(): Promise<string> {
    return await peerConnection.sendCommand<string>('get_platform');
  }

  async listAgentModels(agentType: string): Promise<AgentModelInfo[]> {
    return await peerConnection.sendCommand<AgentModelInfo[]>('list_agent_models', { agentType });
  }

  async readLogs(opts?: { level?: string; limit?: number }): Promise<LogEntry[]> {
    return await peerConnection.sendCommand<LogEntry[]>('read_logs', {
      level: opts?.level,
      limit: opts?.limit,
    });
  }
}

/**
 * Terminal backend implementation for WebRTC (remote machine proxy)
 */
class WebRTCTerminalBackend implements ITerminalBackend {
  // The `?? []` / `?? ''` defaults below are load-bearing, not paranoia: a
  // command whose host handler returns nothing (or a response that arrives
  // without a `data` payload) resolves `sendCommand` to `undefined`. Consumers
  // immediately do `.filter` / `.map` / `.length` on these results during
  // render, so an undefined here surfaces as a render-phase
  // "Cannot read properties of undefined (reading 'length')" in the terminal
  // panel. Normalize to safe empties at the boundary instead.
  async list(): Promise<number[]> {
    return (await peerConnection.sendCommand<number[]>('terminal_list')) ?? [];
  }

  async listSessions(): Promise<import('./IBackend').TerminalSessionData[]> {
    return (await peerConnection.sendCommand<import('./IBackend').TerminalSessionData[]>('terminal_list_sessions')) ?? [];
  }

  async create(cols: number, rows: number, cwd?: string, title?: string, shell?: string, env?: Record<string, string>): Promise<number> {
    return await peerConnection.sendCommand<number>('terminal_create', { cols, rows, cwd: cwd ?? null, title: title ?? null, shell: shell ?? null, env: env ?? null });
  }

  async write(id: number, data: string): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_write', { sessionId: id, data });
  }

  async resize(id: number, cols: number, rows: number): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_resize', { sessionId: id, cols, rows });
  }

  async close(id: number): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_close', { sessionId: id });
  }

  async detach(id: number): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_detach', { sessionId: id });
  }

  async attach(id: number): Promise<string> {
    return (await peerConnection.sendCommand<string>('terminal_attach', { sessionId: id })) ?? '';
  }

  async resume(id: number): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_resume', { sessionId: id });
  }

  async getBuffer(id: number): Promise<string> {
    return (await peerConnection.sendCommand<string>('terminal_get_buffer', { sessionId: id })) ?? '';
  }

  async markAgentSession(id: number, isAgent: boolean, completionPattern?: string): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_mark_agent', {
      sessionId: id,
      isAgent,
      completionPattern: completionPattern ?? null,
    });
  }

  onData(callback: (id: number, data: string) => void): () => void {
    return peerConnection.onEvent((event, payload) => {
      if (event === 'terminal:data') {
        const p = payload as { id: number; data: string };
        callback(p.id, p.data);
      }
    });
  }

  onExit(callback: (id: number) => void): () => void {
    return peerConnection.onEvent((event, payload) => {
      if (event === 'terminal:exit') {
        const p = payload as { id: number };
        callback(p.id);
      }
    });
  }

  onActivity(callback: (id: number) => void): () => void {
    return peerConnection.onEvent((event, payload) => {
      if (event === 'terminal:activity') {
        const p = payload as { id: number };
        callback(p.id);
      }
    });
  }

  onPatternMatch(callback: (id: number) => void): () => void {
    return peerConnection.onEvent((event, payload) => {
      if (event === 'terminal:pattern-match') {
        const p = payload as { id: number };
        callback(p.id);
      }
    });
  }

  async persistAll(): Promise<void> {
    await peerConnection.sendCommand<void>('terminal_persist_all');
  }
}

/**
 * Main WebRTC Backend class
 */
/**
 * Queue backend for the WebRTC (remote-client) surface. The autonomous queue is
 * a local-desktop feature (QueueTab gates on a local backend), so the remote
 * client exposes inert stubs rather than tunneling script runs over the data
 * channel — list ops return empty and run ops are no-ops.
 */
class WebRTCQueueBackend implements IQueueBackend {
  async list(): Promise<QueueTicketSummary[]> { return []; }
  async readTicket(): Promise<string> { return ''; }
  async readLog(): Promise<string> { return ''; }
  async moveTicket(): Promise<void> {}
  async listEpics(): Promise<QueueEpicSummary[]> { return []; }
  async setEngine(): Promise<void> {}
  async submitFeatureRequest(): Promise<{ id: string }> {
    throw new Error('WebRTC: queue.submitFeatureRequest not supported on the remote client');
  }
  async inboxList(): Promise<QueueIdeaSummary[]> { return []; }
  async inboxCreate(): Promise<QueueIdeaSummary> {
    throw new Error('WebRTC: queue.inboxCreate not supported on the remote client');
  }
  async inboxUpdate(): Promise<void> {}
  async inboxDelete(): Promise<void> {}
  async inboxLinkTicket(): Promise<void> {}
  async inboxArchive(): Promise<void> {}
  async invokeScript(): Promise<string> {
    throw new Error('WebRTC: queue runs are not supported on the remote client');
  }
  async startRun(): Promise<void> {
    throw new Error('WebRTC: queue runs are not supported on the remote client');
  }
  async stopRun(): Promise<void> {}
  async listRuns(): Promise<QueueRunInfo[]> { return []; }
  onRunOutput(): () => void { return () => {}; }
  onRunExit(): () => void { return () => {}; }
}

/**
 * Claude identities are a local-desktop concept (they manage on-disk
 * CLAUDE_CONFIG_DIR logins and spawn `claude auth` locally), so they are not
 * meaningful over a remote WebRTC peer. Reads return empty/unauthenticated;
 * mutations reject with a clear message rather than silently no-op.
 */
class WebRTCIdentityBackend implements IIdentityBackend {
  private unsupported(): never {
    throw new Error('Claude identities are not available over a remote (WebRTC) connection — manage them on the host machine.');
  }
  async list(): Promise<ClaudeIdentity[]> { return []; }
  async getDefault(): Promise<ClaudeIdentity | null> { return null; }
  async status(_configDir: string): Promise<AuthStatus> { return { authenticated: false, expiresAt: null, subscriptionType: null }; }
  async upsert(): Promise<void> { this.unsupported(); }
  async remove(): Promise<void> { this.unsupported(); }
  async setDefault(): Promise<void> { this.unsupported(); }
}

class WebRTCCopilotIdentityBackend implements ICopilotIdentityBackend {
  private unsupported(): never {
    throw new Error('Copilot identities are not available over a remote (WebRTC) connection — manage them on the host machine.');
  }
  async list(): Promise<CopilotIdentity[]> { return []; }
  async getDefault(): Promise<CopilotIdentity | null> { return null; }
  async status(_homeDir: string): Promise<CopilotAuthStatus> { return { initialized: false }; }
  async upsert(): Promise<void> { this.unsupported(); }
  async remove(): Promise<void> { this.unsupported(); }
  async setDefault(): Promise<void> { this.unsupported(); }
}

/**
 * Project groups ("Teams") ARE forwarded to the host (unlike identities): the
 * group definitions live in the host's DB and a remote client needs to see them
 * to render the grouped project list correctly. The host dispatches these
 * `group_*` commands through `webrtcHost.ts` to its own CefBackend.groups.
 */
class WebRTCGroupBackend implements IGroupBackend {
  async list(): Promise<ProjectGroup[]> {
    return (await peerConnection.sendCommand<ProjectGroup[]>('group_list')) ?? [];
  }
  async upsert(group: { id: string; name: string; color?: string | null; sortOrder?: number }): Promise<ProjectGroup> {
    return await peerConnection.sendCommand<ProjectGroup>('group_upsert', { group });
  }
  async remove(id: string): Promise<void> {
    await peerConnection.sendCommand<void>('group_delete', { id });
  }
  async reorder(ids: string[]): Promise<void> {
    await peerConnection.sendCommand<void>('group_reorder', { ids });
  }
}

class WebRTCArtifactsBackend implements IArtifactsBackend {
  async list(projectId: string): Promise<ArtifactEntry[]> {
    // Forwarded to the host so a remote/phone client sees the host machine's
    // per-project artifacts (webrtcHost.ts dispatches to its CefBackend).
    return (await peerConnection.sendCommand<ArtifactEntry[]>('artifacts_list', { projectId })) ?? [];
  }
}

export class WebRTCBackend implements IBackend {
  readonly type: BackendType = 'webrtc';

  get isReady(): boolean {
    return peerConnection.isConnected;
  }

  readonly projects: IProjectBackend;
  readonly files: IFileBackend;
  readonly artifacts: IArtifactsBackend;
  readonly transcription: ITranscriptionBackend;
  readonly system: ISystemBackend;
  readonly terminal: ITerminalBackend;
  readonly pipeline: IPipelineBackend;
  readonly queue: IQueueBackend;
  readonly identity: IIdentityBackend;
  readonly copilotIdentity: ICopilotIdentityBackend;
  readonly groups: IGroupBackend;

  constructor() {
    this.projects = new WebRTCProjectBackend();
    this.files = new WebRTCFileBackend();
    this.artifacts = new WebRTCArtifactsBackend();
    this.transcription = new WebRTCTranscriptionBackend();
    this.system = new WebRTCSystemBackend();
    this.terminal = new WebRTCTerminalBackend();
    this.pipeline = new WebRTCPipelineBackend();
    this.queue = new WebRTCQueueBackend();
    this.identity = new WebRTCIdentityBackend();
    this.copilotIdentity = new WebRTCCopilotIdentityBackend();
    this.groups = new WebRTCGroupBackend();
  }
}

/**
 * Create a WebRTCBackend instance
 */
export function createWebRTCBackend(): WebRTCBackend {
  return new WebRTCBackend();
}
